// Build a predicted conference-tournament bracket from the current
// standings. Nearly every D1 women's soccer conference uses one of
// five fixed-bracket shapes keyed off the number of regular-season
// teams that qualify (the `tournament_bids` field on
// catalog.conferences). Each shape is deterministic once the seed
// order is known, so this file is a pure data function: given
// (bids, teamsOrderedBySeed) → bracket structure. The UI component
// in TournamentBracket.tsx walks the structure to render.
//
// We only predict matchups, never winners — downstream rounds carry a
// placeholder referencing the prior round+slot so the UI reads
// "Winner QF1" instead of guessing.

// Entry occupying one side of a matchup. A real seeded team OR a
// forward-reference to the winner of an earlier matchup.
export type BracketEntry =
  | { kind: "team"; seed: number; teamSlug: string; shortName: string }
  | { kind: "winnerOf"; round: number; slot: number };

export type Matchup = {
  round: number; // 1 = earliest played, higher = later
  slot: number; // position within the round (1..N)
  // Global game number, 1-based, assigned left-to-right across the
  // whole bracket (round-major, slot-minor). Rendered as "#1", "#2",
  // etc. in the UI. Matches the labelling convention conference
  // bracket pages use so a user cross-referencing against the real
  // tournament sees the same anchor on both sides.
  gameNumber: number;
  // Optional kickoff date / time strings supplied by the caller when
  // a real tournament schedule is known for this slot. Rendered next
  // to the game number when present; omitted silently otherwise.
  // Pre-formatted: the bracket component does not re-parse.
  date?: string;
  time?: string;
  top: BracketEntry;
  bottom: BracketEntry;
};

export type Round = {
  index: number; // same as Matchup.round
  name: string; // "First Round" / "Quarterfinals" / "Semifinals" / "Final"
  // Optional date / date range string ("November 5-6") rendered
  // under the round title. Pre-formatted by the caller; omitted when
  // no schedule has been wired for this round.
  dateRange?: string;
  matchups: Matchup[];
};

export type Bracket = {
  bids: number;
  rounds: Round[];
};

// BracketSchedule lets the caller overlay real tournament scheduling
// on top of the projected structure. Each entry is matched against
// the corresponding round or matchup and populates the optional
// date/time fields. Keys are the same (round, slot) pair the Matchup
// carries. Missing entries are legal — the bracket just doesn't render
// a date or time for that slot.
export type BracketSchedule = {
  rounds?: Record<number, string>; // round index → date range
  games?: Record<string, { date?: string; time?: string }>; // "round-slot" → schedule
};

// SeedTeam is the input the caller supplies — one per bid, in seed
// order (seed 1 first). Standing rows map onto this shape 1:1.
export type SeedTeam = {
  teamSlug: string;
  shortName: string;
};

// Conferences whose 2026 field size is right but whose bracket a fixed
// seed order cannot project: reseeded rounds (A-10, Horizon, Metro), pods
// that set the semifinal paths (Summit), a stepladder (OVC), divisional
// seeding with no overall order (CAA, UAC), and field sizes with no
// shape yet (SoCon, Southland). Formats change between seasons, so
// recheck this list against each year's brackets. Their standings keep
// the cutoff line; the bracket is hidden rather than shown wrong.
const UNPROJECTABLE_BRACKETS = new Set([
  "atlantic_10",
  "caa",
  "horizon",
  "metro",
  "ovc",
  "socon",
  "southland",
  "summit_league",
  "uac_",
]);

// canProjectBracket takes a conference slug in either hyphen or
// underscore form, because the standings conference field drifts.
export function canProjectBracket(conference: string): boolean {
  return !UNPROJECTABLE_BRACKETS.has(conference.replace(/-/g, "_"));
}

// buildBracket returns null when the field size isn't one of the
// supported standard shapes (4/6/8/10/12). Callers check for null and
// render a short "unsupported" notice rather than guessing a layout.
// Pass `schedule` to overlay real tournament dates/times on top of the
// projected structure — omitted entries render without a date or time.
export function buildBracket(
  bids: number,
  teams: SeedTeam[],
  schedule?: BracketSchedule,
): Bracket | null {
  if (teams.length < bids) return null;
  let b: Bracket | null;
  switch (bids) {
    case 4:
      b = build4(teams);
      break;
    case 6:
      b = build6(teams);
      break;
    case 8:
      b = build8(teams);
      break;
    case 10:
      b = build10(teams);
      break;
    case 12:
      b = build12(teams);
      break;
    default:
      return null;
  }
  applyGameNumbers(b);
  if (schedule) applySchedule(b, schedule);
  return b;
}

// applyGameNumbers assigns sequential #1..#N across all matchups in
// round-major, slot-minor order. Mutates the bracket in place because
// it owns the just-built structure and no other reference exists yet.
function applyGameNumbers(b: Bracket): void {
  let n = 1;
  for (const round of b.rounds) {
    for (const m of round.matchups) {
      m.gameNumber = n++;
    }
  }
}

// applySchedule copies the caller's date/time overlay onto the
// matching round and matchup entries. Rounds without a scheduled
// dateRange stay undefined; matchups without a scheduled game entry
// render just the game number.
function applySchedule(b: Bracket, s: BracketSchedule): void {
  for (const round of b.rounds) {
    if (s.rounds?.[round.index]) {
      round.dateRange = s.rounds[round.index];
    }
    for (const m of round.matchups) {
      const key = `${m.round}-${m.slot}`;
      const entry = s.games?.[key];
      if (entry?.date) m.date = entry.date;
      if (entry?.time) m.time = entry.time;
    }
  }
}

function entry(seed: number, teams: SeedTeam[]): BracketEntry {
  const t = teams[seed - 1];
  return { kind: "team", seed, teamSlug: t.teamSlug, shortName: t.shortName };
}

function winnerOf(round: number, slot: number): BracketEntry {
  return { kind: "winnerOf", round, slot };
}

// 4 teams — straight 2 round single elimination.
// R1 (SF): 1v4, 2v3
// R2 (F): winner(R1·1) v winner(R1·2)
function build4(t: SeedTeam[]): Bracket {
  return {
    bids: 4,
    rounds: [
      {
        index: 1,
        name: "Semifinals",
        matchups: [
          { round: 1, slot: 1, gameNumber: 0, top: entry(1, t), bottom: entry(4, t) },
          { round: 1, slot: 2, gameNumber: 0, top: entry(2, t), bottom: entry(3, t) },
        ],
      },
      {
        index: 2,
        name: "Final",
        matchups: [
          { round: 2, slot: 1, gameNumber: 0, top: winnerOf(1, 1), bottom: winnerOf(1, 2) },
        ],
      },
    ],
  };
}

// 6 teams — top 2 seeds get first-round byes.
// R1 (QF): 4v5 → feeds seed 1 side; 3v6 → feeds seed 2 side
// R2 (SF): 1 v winner(R1·1), 2 v winner(R1·2)
// R3 (F)
function build6(t: SeedTeam[]): Bracket {
  return {
    bids: 6,
    rounds: [
      {
        index: 1,
        name: "First Round",
        matchups: [
          { round: 1, slot: 1, gameNumber: 0, top: entry(4, t), bottom: entry(5, t) },
          { round: 1, slot: 2, gameNumber: 0, top: entry(3, t), bottom: entry(6, t) },
        ],
      },
      {
        index: 2,
        name: "Semifinals",
        matchups: [
          { round: 2, slot: 1, gameNumber: 0, top: entry(1, t), bottom: winnerOf(1, 1) },
          { round: 2, slot: 2, gameNumber: 0, top: entry(2, t), bottom: winnerOf(1, 2) },
        ],
      },
      {
        index: 3,
        name: "Final",
        matchups: [
          { round: 3, slot: 1, gameNumber: 0, top: winnerOf(2, 1), bottom: winnerOf(2, 2) },
        ],
      },
    ],
  };
}

// 8 teams — standard quarters/semis/final.
// R1 (QF): 1v8, 4v5, 3v6, 2v7 (slots arranged so 1 and 2 land on
//           opposite halves of the bracket)
// R2 (SF): winner(QF1) v winner(QF2); winner(QF4) v winner(QF3)
// R3 (F)
function build8(t: SeedTeam[]): Bracket {
  return {
    bids: 8,
    rounds: [
      {
        index: 1,
        name: "Quarterfinals",
        matchups: [
          { round: 1, slot: 1, gameNumber: 0, top: entry(1, t), bottom: entry(8, t) },
          { round: 1, slot: 2, gameNumber: 0, top: entry(4, t), bottom: entry(5, t) },
          { round: 1, slot: 3, gameNumber: 0, top: entry(3, t), bottom: entry(6, t) },
          { round: 1, slot: 4, gameNumber: 0, top: entry(2, t), bottom: entry(7, t) },
        ],
      },
      {
        index: 2,
        name: "Semifinals",
        matchups: [
          { round: 2, slot: 1, gameNumber: 0, top: winnerOf(1, 1), bottom: winnerOf(1, 2) },
          { round: 2, slot: 2, gameNumber: 0, top: winnerOf(1, 4), bottom: winnerOf(1, 3) },
        ],
      },
      {
        index: 3,
        name: "Final",
        matchups: [
          { round: 3, slot: 1, gameNumber: 0, top: winnerOf(2, 1), bottom: winnerOf(2, 2) },
        ],
      },
    ],
  };
}

// 10 teams — top 6 seeds bye; 7-10 play into quarters.
// R1: 8v9 → feeds QF slot 1 (against seed 1)
// R1: 7v10 → feeds QF slot 4 (against seed 2)
// R2 (QF): 1 v w(R1·1), 4v5, 3v6, 2 v w(R1·2)
function build10(t: SeedTeam[]): Bracket {
  return {
    bids: 10,
    rounds: [
      {
        index: 1,
        name: "First Round",
        matchups: [
          { round: 1, slot: 1, gameNumber: 0, top: entry(8, t), bottom: entry(9, t) },
          { round: 1, slot: 2, gameNumber: 0, top: entry(7, t), bottom: entry(10, t) },
        ],
      },
      {
        index: 2,
        name: "Quarterfinals",
        matchups: [
          { round: 2, slot: 1, gameNumber: 0, top: entry(1, t), bottom: winnerOf(1, 1) },
          { round: 2, slot: 2, gameNumber: 0, top: entry(4, t), bottom: entry(5, t) },
          { round: 2, slot: 3, gameNumber: 0, top: entry(3, t), bottom: entry(6, t) },
          { round: 2, slot: 4, gameNumber: 0, top: entry(2, t), bottom: winnerOf(1, 2) },
        ],
      },
      {
        index: 3,
        name: "Semifinals",
        matchups: [
          { round: 3, slot: 1, gameNumber: 0, top: winnerOf(2, 1), bottom: winnerOf(2, 2) },
          { round: 3, slot: 2, gameNumber: 0, top: winnerOf(2, 4), bottom: winnerOf(2, 3) },
        ],
      },
      {
        index: 4,
        name: "Final",
        matchups: [
          { round: 4, slot: 1, gameNumber: 0, top: winnerOf(3, 1), bottom: winnerOf(3, 2) },
        ],
      },
    ],
  };
}

// 12 teams — top 4 bye; 5-12 play into quarters.
// R1: 8v9, 5v12, 6v11, 7v10 (slot order chosen so each winner feeds
//      the matching quarter: QF1 opposite seed 1, QF2 opposite seed 4,
//      QF3 opposite seed 3, QF4 opposite seed 2)
// R2 (QF): 1 v w(R1·1), 4 v w(R1·2), 3 v w(R1·3), 2 v w(R1·4)
function build12(t: SeedTeam[]): Bracket {
  return {
    bids: 12,
    rounds: [
      {
        index: 1,
        name: "First Round",
        matchups: [
          { round: 1, slot: 1, gameNumber: 0, top: entry(8, t), bottom: entry(9, t) },
          { round: 1, slot: 2, gameNumber: 0, top: entry(5, t), bottom: entry(12, t) },
          { round: 1, slot: 3, gameNumber: 0, top: entry(6, t), bottom: entry(11, t) },
          { round: 1, slot: 4, gameNumber: 0, top: entry(7, t), bottom: entry(10, t) },
        ],
      },
      {
        index: 2,
        name: "Quarterfinals",
        matchups: [
          { round: 2, slot: 1, gameNumber: 0, top: entry(1, t), bottom: winnerOf(1, 1) },
          { round: 2, slot: 2, gameNumber: 0, top: entry(4, t), bottom: winnerOf(1, 2) },
          { round: 2, slot: 3, gameNumber: 0, top: entry(3, t), bottom: winnerOf(1, 3) },
          { round: 2, slot: 4, gameNumber: 0, top: entry(2, t), bottom: winnerOf(1, 4) },
        ],
      },
      {
        index: 3,
        name: "Semifinals",
        matchups: [
          { round: 3, slot: 1, gameNumber: 0, top: winnerOf(2, 1), bottom: winnerOf(2, 2) },
          { round: 3, slot: 2, gameNumber: 0, top: winnerOf(2, 4), bottom: winnerOf(2, 3) },
        ],
      },
      {
        index: 4,
        name: "Final",
        matchups: [
          { round: 4, slot: 1, gameNumber: 0, top: winnerOf(3, 1), bottom: winnerOf(3, 2) },
        ],
      },
    ],
  };
}

const SINGULAR_ROUND: Record<string, string> = {
  Quarterfinals: "Quarterfinal",
  Semifinals: "Semifinal",
};

// winnerLabel names the earlier game a "winnerOf" entry waits on, using
// the bracket's own round names because a round's number means different
// things across field sizes: round 1 is the quarterfinals of an 8-team
// bracket but the first round of a 12-team one.
export function winnerLabel(
  bracket: Bracket,
  entry: Extract<BracketEntry, { kind: "winnerOf" }>,
): string {
  const round = bracket.rounds.find((r) => r.index === entry.round);
  if (!round) {
    throw new Error(`invariant: bracket has no round ${entry.round}`);
  }
  const name = SINGULAR_ROUND[round.name] ?? round.name;
  return `Winner of ${name} ${entry.slot}`;
}

